import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { purchaseOrdersAPI, contentPOsAPI, taxonomyAPI, customFieldsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { scrollAndFlash } from '../lib/highlight';
import { renderCustomValue } from '../components/shared/EntityCustomFields';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Download, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { POFormDialog } from '../components/purchase-orders/POFormDialog';
import { ContentPOFormDialog } from '../components/purchase-orders/ContentPOFormDialog';
import { POSummaryCards } from '../components/purchase-orders/POSummaryCards'; // eslint-disable-line no-unused-vars
import { POAlertBanners } from '../components/purchase-orders/POAlertBanners';
import { POFiltersBar } from '../components/purchase-orders/POFiltersBar';
import { POTable } from '../components/purchase-orders/POTable';
import { AddExtensionDialog } from '../components/purchase-orders/AddExtensionDialog';
import { ExtensionsListDialog } from '../components/purchase-orders/ExtensionsListDialog';
import { MonthlyConsumptionChart } from '../components/purchase-orders/MonthlyConsumptionChart';
import { PODashboardTab } from '../components/purchase-orders/PODashboardTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const EMPTY_PO_FORM = {
  po_number: '', short_title: '', total_amount: '', currency: 'USD',
  issue_date: new Date().toISOString().split('T')[0], expiry_date: '', description: '', sow_reference: '',
  po_type: 'standalone', parent_po_id: '', workload_type: 'Training Delivery',
  ms_owner: '', line_items: [], custom_fields: {}, document_path: '', document_filename: '', document_content_type: '',
};

const CONTENT_WORKLOAD_VALUE = 'Content Development';

const EMPTY_CONTENT_PO_FORM = {
  po_number: '', short_title: '', total_amount: '',
  issue_date: new Date().toISOString().split('T')[0], expiry_date: '', description: '',
  ms_owner: '', workload_type: CONTENT_WORKLOAD_VALUE, line_items: [], custom_fields: {}, document_path: '', document_filename: '', document_content_type: '',
};

const EMPTY_EXTENSION_FORM = {
  po_number: '', short_title: '', amount: '', issue_date: new Date().toISOString().split('T')[0], expiry_date: '', sow_reference: '',
  description: '', ms_owner: '', line_items: [], document_path: '', document_filename: '', document_content_type: '',
};

export const PurchaseOrdersPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [pos, setPos] = useState([]);
  const [contentPos, setContentPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  useEffect(() => { const q = searchParams.get('q'); if (q) setSearch(q); }, [searchParams]);
  useEffect(() => {
    const hid = searchParams.get('highlight');
    if (!hid || !pos.length) return;
    const t = setTimeout(() => scrollAndFlash(`po-row-${hid}`), 350);
    return () => clearTimeout(t);
  }, [searchParams, pos]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWorkload, setFilterWorkload] = useState('all');
  const [colFilters, setColFilters] = useState({ status: new Set(), workload: new Set(), progress: new Set(), ms_owner: new Set() });
  const setColFilter = (key, s) => setColFilters(prev => ({ ...prev, [key]: s }));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PO_FORM);
  const [monthlyConsumption, setMonthlyConsumption] = useState(null);
  const [workloadTypeOptions, setWorkloadTypeOptions] = useState([]);
  const [poFields, setPoFields] = useState([]);
  const loadPoFields = () => customFieldsAPI.list('purchase_order').then(setPoFields).catch(() => {});

  // Content PO dialog state (combined table)
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [editingContentPo, setEditingContentPo] = useState(null);
  const [contentPoForm, setContentPoForm] = useState(EMPTY_CONTENT_PO_FORM);
  const [savingContentPo, setSavingContentPo] = useState(false);

  // Extension PO state
  const [isExtensionDialogOpen, setIsExtensionDialogOpen] = useState(false);
  const [extensionParentPo, setExtensionParentPo] = useState(null);
  const [extensionForm, setExtensionForm] = useState(EMPTY_EXTENSION_FORM);
  const [isExtensionsListOpen, setIsExtensionsListOpen] = useState(false);
  const [viewingExtensionsPo, setViewingExtensionsPo] = useState(null);
  const [extensionsList, setExtensionsList] = useState([]);

  // Cleanup (delete) state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteSingleTarget, setDeleteSingleTarget] = useState(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
    loadMonthlyConsumption();
    loadWorkloadTypeOptions();
    loadPoFields();
  }, []);

  const loadWorkloadTypeOptions = async () => {
    try {
      const cat = await taxonomyAPI.getCategory('po_workload_type');
      setWorkloadTypeOptions((cat.items || []).filter(i => i.active));
    } catch (error) {
      console.error('Failed to load PO Workload Type taxonomy:', error);
    }
  };

  const loadMonthlyConsumption = async () => {
    try {
      const data = await purchaseOrdersAPI.getMonthlyConsumption();
      setMonthlyConsumption(data);
    } catch (error) {
      console.error('Failed to load monthly consumption:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [posData, contentPosData] = await Promise.all([
        purchaseOrdersAPI.getAll({ include_extensions: true }),
        contentPOsAPI.getAll({ include_extensions: true }),
      ]);
      setPos(posData);
      setContentPos(contentPosData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load Purchase Orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (po = null) => {
    if (po) {
      setEditingPo(po);
      setFormData({
        po_number: po.po_number,
        short_title: po.short_title || '',
        total_amount: po.total_amount.toString(),
        currency: po.currency || 'USD',
        issue_date: po.issue_date,
        expiry_date: po.expiry_date || '',
        description: po.description || '',
        sow_reference: po.sow_reference || '',
        po_type: 'standalone',
        parent_po_id: '',
        workload_type: po.workload_type || 'Training Delivery',
        ms_owner: po.ms_owner || '',
        line_items: (po.line_items || []).map(li => ({ ...li, amount: li.amount?.toString() ?? '' })),
        custom_fields: po.custom_fields || {},
        document_path: po.document_path || '',
        document_filename: po.document_filename || '',
        document_content_type: po.document_content_type || '',
      });
    } else {
      setEditingPo(null);
      setFormData(EMPTY_PO_FORM);
    }
    setIsDialogOpen(true);
  };

  const handleExtracted = (result) => {
    const ex = result.extracted || {};
    setFormData(prev => ({
      ...prev,
      po_number: ex.po_number || prev.po_number,
      short_title: ex.short_title || prev.short_title,
      total_amount: ex.total_amount != null ? String(ex.total_amount) : prev.total_amount,
      currency: ex.currency || prev.currency,
      issue_date: ex.issue_date || prev.issue_date,
      expiry_date: ex.expiry_date || prev.expiry_date,
      sow_reference: ex.sow_reference || prev.sow_reference,
      description: ex.description || prev.description,
      ms_owner: ex.ms_owner || prev.ms_owner,
      line_items: Array.isArray(ex.line_items) && ex.line_items.length > 0
        ? ex.line_items.map(li => ({
            description: li.description || '',
            delivery_date: li.delivery_date || '',
            amount: li.amount != null ? String(li.amount) : '',
          }))
        : prev.line_items,
      document_path: result.document_path || prev.document_path,
      document_filename: result.document_filename || prev.document_filename,
      document_content_type: result.document_content_type || prev.document_content_type,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Unified "New PO" form — Workload Type decides which PO system this creates into
    if (!editingPo && formData.workload_type === CONTENT_WORKLOAD_VALUE) {
      if (!formData.po_number || !formData.total_amount) {
        toast.error('Please fill in all required fields');
        return;
      }
      try {
        const payload = {
          po_number: formData.po_number,
          short_title: formData.short_title || null,
          total_amount: parseFloat(formData.total_amount),
          issue_date: formData.issue_date,
          expiry_date: formData.expiry_date || null,
          description: formData.description || null,
          ms_owner: formData.ms_owner || null,
          workload_type: formData.workload_type || null,
          custom_fields: formData.custom_fields || {},
          line_items: (formData.line_items || [])
            .filter(li => li.description)
            .map(li => ({
              description: li.description,
              delivery_date: li.delivery_date || null,
              amount: parseFloat(li.amount) || 0,
              delivered: !!li.delivered,
              invoiced: !!li.invoiced,
            delivered: !!li.delivered,
            invoiced: !!li.invoiced,
            })),
          document_path: formData.document_path || null,
          document_filename: formData.document_filename || null,
          document_content_type: formData.document_content_type || null,
        };
        await contentPOsAPI.create(payload);
        toast.success('Content PO created');
        setIsDialogOpen(false);
        loadData();
      } catch (error) {
        console.error('Failed to create Content PO:', error);
        toast.error(error.response?.data?.detail || 'Failed to create Content PO');
      }
      return;
    }

    if (formData.po_type === 'extension' && !editingPo) {
      if (!formData.parent_po_id || !formData.po_number || !formData.total_amount) {
        toast.error('Please select a parent PO and fill in the extension number and amount');
        return;
      }
      try {
        const parentPo = pos.find(p => p.id === formData.parent_po_id);
        await purchaseOrdersAPI.addExtension(formData.parent_po_id, {
          po_number: formData.po_number,
          short_title: formData.short_title || null,
          amount: parseFloat(formData.total_amount),
          currency: formData.currency,
          issue_date: formData.issue_date,
          expiry_date: formData.expiry_date || null,
          sow_reference: formData.sow_reference || null,
          description: formData.description || null,
          ms_owner: formData.ms_owner || null,
          line_items: (formData.line_items || [])
            .filter(li => li.description)
            .map(li => ({
              description: li.description,
              delivery_date: li.delivery_date || null,
              amount: parseFloat(li.amount) || 0,
              delivered: !!li.delivered,
              invoiced: !!li.invoiced,
            delivered: !!li.delivered,
            invoiced: !!li.invoiced,
            })),
          document_path: formData.document_path || null,
          document_filename: formData.document_filename || null,
          document_content_type: formData.document_content_type || null,
        });
        toast.success(`Extension added — ${parentPo?.po_number} total updated`);
        setIsDialogOpen(false);
        loadData();
      } catch (error) {
        console.error('Failed to add extension:', error);
        toast.error(error.response?.data?.detail || 'Failed to add extension');
      }
      return;
    }

    if (!formData.po_number || !formData.total_amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        expiry_date: formData.expiry_date || null,
        tsp_id: null,
        line_items: (formData.line_items || [])
          .filter(li => li.description)
          .map(li => ({
            description: li.description,
            delivery_date: li.delivery_date || null,
            amount: parseFloat(li.amount) || 0,
            delivered: !!li.delivered,
            invoiced: !!li.invoiced,
          })),
        document_path: formData.document_path || null,
        document_filename: formData.document_filename || null,
        document_content_type: formData.document_content_type || null,
      };
      delete payload.po_type;
      delete payload.parent_po_id;

      if (editingPo) {
        await purchaseOrdersAPI.update(editingPo.id, payload);
        toast.success('Purchase Order updated');
      } else {
        await purchaseOrdersAPI.create(payload);
        toast.success('Purchase Order created');
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Purchase Order');
    }
  };

  const handleCancel = async (po) => {
    const isContent = po._po_type === 'content';
    if (!window.confirm(`Are you sure you want to ${isContent ? 'close' : 'cancel'} this Purchase Order?`)) return;

    try {
      if (isContent) {
        await contentPOsAPI.close(po.id);
      } else {
        await purchaseOrdersAPI.cancel(po.id);
      }
      toast.success(`Purchase Order ${isContent ? 'closed' : 'cancelled'}`);
      loadData();
    } catch (error) {
      console.error('Failed to cancel PO:', error);
      toast.error(`Failed to ${isContent ? 'close' : 'cancel'} Purchase Order`);
    }
  };

  const openContentPoEdit = async (po) => {
    try {
      const detail = await contentPOsAPI.getById(po.id);
      setEditingContentPo(detail);
      setContentPoForm({
        po_number: detail.po_number,
        short_title: detail.short_title || '',
        total_amount: detail.total_amount.toString(),
        issue_date: detail.issue_date,
        expiry_date: detail.expiry_date || '',
        description: detail.description || '',
        ms_owner: detail.ms_owner || '',
        workload_type: detail.workload_type || CONTENT_WORKLOAD_VALUE,
        line_items: (detail.line_items || []).map(li => ({ ...li, amount: li.amount?.toString() ?? '' })),
        custom_fields: detail.custom_fields || {},
        document_path: detail.document_path || '',
        document_filename: detail.document_filename || '',
        document_content_type: detail.document_content_type || '',
      });
      setIsContentDialogOpen(true);
    } catch (error) {
      console.error('Failed to load Content PO:', error);
      toast.error('Failed to load Content PO details');
    }
  };

  const handleEditRow = (po) => {
    if (po._po_type === 'content') {
      openContentPoEdit(po);
    } else {
      handleOpenDialog(po);
    }
  };

  const handleContentPoExtracted = (result) => {
    const ex = result.extracted || {};
    setContentPoForm(prev => ({
      ...prev,
      po_number: ex.po_number || prev.po_number,
      short_title: ex.short_title || prev.short_title,
      total_amount: ex.total_amount != null ? String(ex.total_amount) : prev.total_amount,
      issue_date: ex.issue_date || prev.issue_date,
      expiry_date: ex.expiry_date || prev.expiry_date,
      description: ex.description || prev.description,
      ms_owner: ex.ms_owner || prev.ms_owner,
      line_items: Array.isArray(ex.line_items) && ex.line_items.length > 0
        ? ex.line_items.map(li => ({
            description: li.description || '',
            delivery_date: li.delivery_date || '',
            amount: li.amount != null ? String(li.amount) : '',
          }))
        : prev.line_items,
      document_path: result.document_path || prev.document_path,
      document_filename: result.document_filename || prev.document_filename,
      document_content_type: result.document_content_type || prev.document_content_type,
    }));
  };

  const handleContentPoSubmit = async () => {
    if (!contentPoForm.po_number || !contentPoForm.total_amount || !contentPoForm.issue_date) {
      toast.error('Please fill in PO number, amount and issue date');
      return;
    }
    setSavingContentPo(true);
    try {
      const payload = {
        po_number: contentPoForm.po_number,
        short_title: contentPoForm.short_title || null,
        total_amount: parseFloat(contentPoForm.total_amount),
        issue_date: contentPoForm.issue_date,
        expiry_date: contentPoForm.expiry_date || null,
        description: contentPoForm.description || null,
        ms_owner: contentPoForm.ms_owner || null,
        workload_type: contentPoForm.workload_type || null,
        custom_fields: contentPoForm.custom_fields || {},
        line_items: (contentPoForm.line_items || [])
          .filter(li => li.description)
          .map(li => ({
            description: li.description,
            delivery_date: li.delivery_date || null,
            amount: parseFloat(li.amount) || 0,
            delivered: !!li.delivered,
            invoiced: !!li.invoiced,
          })),
        document_path: contentPoForm.document_path || null,
        document_filename: contentPoForm.document_filename || null,
        document_content_type: contentPoForm.document_content_type || null,
      };
      if (editingContentPo) {
        await contentPOsAPI.update(editingContentPo.id, payload);
        toast.success('Content PO updated');
      } else {
        await contentPOsAPI.create(payload);
        toast.success('Content PO created');
      }
      setIsContentDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save Content PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Content PO');
    } finally {
      setSavingContentPo(false);
    }
  };

  const openExtensionDialog = (po) => {
    setExtensionParentPo(po);
    setExtensionForm(EMPTY_EXTENSION_FORM);
    setIsExtensionDialogOpen(true);
  };

  const handleExtensionExtracted = (result) => {
    const ex = result.extracted || {};
    setExtensionForm(prev => ({
      ...prev,
      po_number: ex.po_number || prev.po_number,
      short_title: ex.short_title || prev.short_title,
      amount: ex.total_amount != null ? String(ex.total_amount) : prev.amount,
      issue_date: ex.issue_date || prev.issue_date,
      expiry_date: ex.expiry_date || prev.expiry_date,
      sow_reference: ex.sow_reference || prev.sow_reference,
      description: ex.description || prev.description,
      ms_owner: ex.ms_owner || prev.ms_owner,
      line_items: Array.isArray(ex.line_items) && ex.line_items.length > 0
        ? ex.line_items.map(li => ({
            description: li.description || '',
            delivery_date: li.delivery_date || '',
            amount: li.amount != null ? String(li.amount) : '',
          }))
        : prev.line_items,
      document_path: result.document_path || prev.document_path,
      document_filename: result.document_filename || prev.document_filename,
      document_content_type: result.document_content_type || prev.document_content_type,
      detected_parent_po_number: ex.parent_po_number || prev.detected_parent_po_number,
    }));
  };

  const handleExtensionSubmit = async (e) => {
    e.preventDefault();
    if (!extensionForm.po_number || !extensionForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    const api = extensionParentPo._po_type === 'content' ? contentPOsAPI : purchaseOrdersAPI;
    try {
      await api.addExtension(extensionParentPo.id, {
        po_number: extensionForm.po_number,
        short_title: extensionForm.short_title || null,
        amount: parseFloat(extensionForm.amount),
        issue_date: extensionForm.issue_date,
        expiry_date: extensionForm.expiry_date || null,
        sow_reference: extensionForm.sow_reference || null,
        description: extensionForm.description || null,
        ms_owner: extensionForm.ms_owner || null,
        line_items: (extensionForm.line_items || [])
          .filter(li => li.description)
          .map(li => ({
            description: li.description,
            delivery_date: li.delivery_date || null,
            amount: parseFloat(li.amount) || 0,
            delivered: !!li.delivered,
            invoiced: !!li.invoiced,
          })),
        document_path: extensionForm.document_path || null,
        document_filename: extensionForm.document_filename || null,
        document_content_type: extensionForm.document_content_type || null,
      });
      toast.success(`Extension added — ${extensionParentPo.po_number} total updated`);
      setIsExtensionDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to add extension:', error);
      toast.error(error.response?.data?.detail || 'Failed to add extension');
    }
  };

  const openExtensionsList = async (po) => {
    setViewingExtensionsPo(po);
    setIsExtensionsListOpen(true);
    const api = po._po_type === 'content' ? contentPOsAPI : purchaseOrdersAPI;
    try {
      const list = await api.getExtensions(po.id);
      setExtensionsList(list);
    } catch (error) {
      console.error('Failed to load extensions:', error);
      setExtensionsList([]);
    }
  };

  const handleDeleteExtension = async (extensionId) => {
    if (!window.confirm('Remove this extension? The parent PO total will decrease accordingly.')) return;
    const api = viewingExtensionsPo._po_type === 'content' ? contentPOsAPI : purchaseOrdersAPI;
    try {
      await api.deleteExtension(viewingExtensionsPo.id, extensionId);
      toast.success('Extension removed');
      const list = await api.getExtensions(viewingExtensionsPo.id);
      setExtensionsList(list);
      loadData();
    } catch (error) {
      console.error('Failed to remove extension:', error);
      toast.error('Failed to remove extension');
    }
  };

  const handleExportExtensionHistory = async () => {
    try {
      const blob = await purchaseOrdersAPI.exportExtensionHistory();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_extension_history_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Extension history exported');
    } catch (error) {
      console.error('Failed to export extension history:', error);
      toast.error('Failed to export extension history');
    }
  };

  const handleExportUtilisation = async () => {
    try {
      const blob = await purchaseOrdersAPI.exportUtilisation();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_utilisation_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PO utilisation report exported');
    } catch (error) {
      console.error('Failed to export utilisation report:', error);
      toast.error('Failed to export utilisation report');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked, visibleIds) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => checked ? next.add(id) : next.delete(id));
      return next;
    });
  };

  const handleToggleLineItem = async (po, idx, field, value) => {
    const api = po._po_type === 'content' ? contentPOsAPI : purchaseOrdersAPI;
    const newItems = (po.line_items || []).map((li, i) => (i === idx ? { ...li, [field]: value } : li));
    // optimistic UI update
    const apply = (arr) => arr.map(p => (p.id === po.id ? { ...p, line_items: newItems } : p));
    if (po._po_type === 'content') setContentPos(prev => apply(prev)); else setPos(prev => apply(prev));
    try {
      await api.update(po.id, { line_items: newItems });
      loadData();
    } catch (error) {
      toast.error('Failed to update line item');
      loadData();
    }
  };

  const handleDeleteSingle = async () => {
    if (!deleteSingleTarget) return;
    setDeleting(true);
    try {
      const api = deleteSingleTarget._po_type === 'content' ? contentPOsAPI : purchaseOrdersAPI;
      await api.deletePermanent(deleteSingleTarget.id, isSuperAdmin);
      toast.success(`"${deleteSingleTarget.po_number}" deleted`);
      setDeleteSingleTarget(null);
      setSelectedIds(prev => { const next = new Set(prev); next.delete(deleteSingleTarget.id); return next; });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete Purchase Order');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      const result = await purchaseOrdersAPI.bulkDelete(Array.from(selectedIds));
      toast.success(result.message);
      if (result.skipped?.length > 0) {
        result.skipped.forEach(s => toast.warning(`${s.po_number}: ${s.reason}`));
      }
      setSelectedIds(new Set());
      setBulkDeleteConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete Purchase Orders');
    } finally {
      setDeleting(false);
    }
  };

  const poMatchesSearch = (po, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    const fields = [po.po_number, po.short_title, po.description, po.ms_owner, po.sow_reference, po.parent_po_number];
    if (fields.some(v => v && v.toLowerCase().includes(s))) return true;
    return (po.line_items || []).some(li => (li.description && li.description.toLowerCase().includes(s)) || (li.notes && li.notes.toLowerCase().includes(s)));
  };

  const matchCol = (po) => {
    if (colFilters.status.size && !colFilters.status.has(po.status)) return false;
    if (colFilters.workload.size && !colFilters.workload.has(po.workload_type)) return false;
    if (colFilters.progress.size && !colFilters.progress.has(po.work_progress)) return false;
    if (colFilters.ms_owner.size && !colFilters.ms_owner.has(po.ms_owner || '')) return false;
    return true;
  };

  const filteredPos = pos.filter(po => {
    if (po.po_type === 'extension') return false; // extensions roll up into parent for KPI totals
    if (!poMatchesSearch(po, search)) return false;
    return matchCol(po);
  });

  // Combined table (Delivery + Content POs) — UI-only merge, backends stay separate
  const combinedPos = [
    ...pos.map(po => ({ ...po, _po_type: 'delivery' })),
    ...contentPos.map(po => ({ ...po, _po_type: 'content' })),
  ];
  const filteredCombinedPos = combinedPos.filter(po => {
    if (!poMatchesSearch(po, search)) return false;
    return matchCol(po);
  });

  const statusOpts = [...new Set(combinedPos.map(po => po.status).filter(Boolean))].sort().map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  const workloadOpts = workloadTypeOptions.filter(o => combinedPos.some(po => po.workload_type === o.value)).map(o => ({ value: o.value, label: o.label }));
  const msOwnerOpts = [...new Set(combinedPos.map(po => po.ms_owner).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const progressOpts = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
  ];

  // At-risk POs: active POs with utilization > 80%
  const atRiskPos = filteredPos.filter(po => po.status === 'active' && (po.utilization_percentage || 0) >= 80);

  // Exhausted POs: fully consumed and awaiting an extension to top-up
  const exhaustedPos = filteredPos.filter(po => po.status === 'exhausted');

  // POs whose validity (last delivery date) is within 30 days — renewal reminder
  const expiringPos = filteredCombinedPos.filter(po => po.validity_expiring_soon)
    .sort((a, b) => (a.validity_days_left ?? 999) - (b.validity_days_left ?? 999));

  // POs / Extensions created in the last 7 days
  const _sevenDaysAgo = new Date();
  _sevenDaysAgo.setDate(_sevenDaysAgo.getDate() - 7);
  const recentPos = combinedPos.filter(po => po.created_at && new Date(po.created_at) >= _sevenDaysAgo)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Calculate totals (consolidated total incl. extensions) — Delivery POs only
  const totalValue = filteredPos.reduce((sum, po) => sum + (po.consolidated_total_amount ?? po.total_amount ?? 0), 0);
  const totalUsed = filteredPos.reduce((sum, po) => sum + (po.used_amount || 0), 0);
  const totalAvailable = totalValue - totalUsed;

  const statusCounts = {
    all: combinedPos.length,
    active: combinedPos.filter(po => po.status === 'active').length,
    exhausted: combinedPos.filter(po => po.status === 'exhausted').length,
    cancelled: combinedPos.filter(po => po.status === 'cancelled').length,
  };

  const workloadCounts = { all: combinedPos.length };
  workloadTypeOptions.forEach(opt => {
    workloadCounts[opt.value] = combinedPos.filter(po => po.workload_type === opt.value).length;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold" data-testid="purchase-orders-page-title">Purchase Orders</h1>
          <p className="text-muted-foreground">Manage Delivery &amp; Content POs — one combined table</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-pos-button">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedIds.size})
            </Button>
          )}
          <POFormDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            editingPo={editingPo}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onNew={() => handleOpenDialog()}
            parentPoOptions={pos.filter(p => p.status !== 'cancelled')}
            onExtracted={handleExtracted}
            workloadTypeOptions={workloadTypeOptions}
            onSchemaChanged={loadPoFields}
            canCreate={isSuperAdmin}
          />
        </div>
      </div>

      <Tabs defaultValue="table">
        <TabsList data-testid="po-page-tabs">
          <TabsTrigger value="table" data-testid="po-tab-table">Table</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="po-tab-dashboard">Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-6 mt-4">
          <POAlertBanners atRiskPos={atRiskPos} exhaustedPos={exhaustedPos} expiringPos={expiringPos} recentPos={recentPos} onAddExtension={openExtensionDialog} />

          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search PO number, title, MS owner, description or line items..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="po-search" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">Tip: click a column header (Status, Workload, Progress) to filter by that field.</p>
            </CardContent>
          </Card>

          <ActiveFiltersBar
            filters={[
              { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
              { key: 'workload', label: 'Workload', set: colFilters.workload, options: workloadOpts, onChange: (s) => setColFilter('workload', s) },
              { key: 'ms_owner', label: 'MS Owner', set: colFilters.ms_owner, options: msOwnerOpts, onChange: (s) => setColFilter('ms_owner', s) },
              { key: 'progress', label: 'Progress', set: colFilters.progress, options: progressOpts, onChange: (s) => setColFilter('progress', s) },
            ]}
            search={search}
            onClearSearch={() => setSearch('')}
            testId="po-active-filters"
          />

          <POTable
            pos={filteredCombinedPos}
            loading={loading}
            search={search}
            filterStatus={filterStatus}
            onNewPo={() => handleOpenDialog()}
            onEdit={handleEditRow}
            onAddExtension={openExtensionDialog}
            onCancel={handleCancel}
            onViewExtensions={openExtensionsList}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            onDeleteSingle={setDeleteSingleTarget}
            workloadTypeOptions={workloadTypeOptions}
            colFilters={colFilters}
            setColFilter={setColFilter}
            statusOpts={statusOpts}
            workloadOpts={workloadOpts}
            msOwnerOpts={msOwnerOpts}
            progressOpts={progressOpts}
            customFields={poFields}
            isSuperAdmin={isSuperAdmin}
            onToggleLineItem={handleToggleLineItem}
          />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4">
          <PODashboardTab pos={pos} totalValue={totalValue} totalUsed={totalUsed} totalAvailable={totalAvailable} combinedPos={combinedPos} workloadTypeOptions={workloadTypeOptions} />
        </TabsContent>
      </Tabs>

      <ContentPOFormDialog
        open={isContentDialogOpen}
        onOpenChange={setIsContentDialogOpen}
        editingPo={editingContentPo}
        form={contentPoForm}
        setForm={setContentPoForm}
        onSubmit={handleContentPoSubmit}
        onExtracted={handleContentPoExtracted}
        saving={savingContentPo}
        workloadTypeOptions={workloadTypeOptions}
        onSchemaChanged={loadPoFields}
      />

      <AddExtensionDialog
        open={isExtensionDialogOpen}
        onOpenChange={setIsExtensionDialogOpen}
        parentPo={extensionParentPo}
        form={extensionForm}
        setForm={setExtensionForm}
        onSubmit={handleExtensionSubmit}
        onExtracted={handleExtensionExtracted}
      />

      <ExtensionsListDialog
        open={isExtensionsListOpen}
        onOpenChange={setIsExtensionsListOpen}
        po={viewingExtensionsPo}
        extensions={extensionsList}
        onDelete={handleDeleteExtension}
      />

      <AlertDialog open={!!deleteSingleTarget} onOpenChange={(o) => !o && setDeleteSingleTarget(null)}>
        <AlertDialogContent data-testid="delete-po-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteSingleTarget?.po_number}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the PO and its extensions. This cannot be undone.
              {isSuperAdmin ? (
                (deleteSingleTarget?.used_amount || 0) > 0
                  ? ' ⚠️ This PO has billed usage tagged against it. As a Super Admin you can still delete it, but the utilization history will be lost.'
                  : ' This PO has no billed usage.'
              ) : ' Blocked automatically if the PO has any billed usage — cancel it instead in that case.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-po-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSingle} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="delete-po-confirm-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <AlertDialogContent data-testid="bulk-delete-po-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} selected Purchase Order(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes each selected PO and its extensions. Any PO with real billed usage
              will be automatically skipped to protect the audit trail — you'll see which ones were skipped and why.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-po-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-po-confirm-btn">
              {deleting ? 'Deleting...' : 'Delete Selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchaseOrdersPage;
